import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import { LabelGroupQueryKeys } from '~settings/api/config/queryKeys';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { getRequestHeaders } from '~settings/api/lib/headers';
import type { PresetUseMutationOptions } from '~settings/api/lib/types';

interface DeleteLabelGroupVariables {
  id: string;
  name: string;
}

export const useLabelGroupDelete = (
  options?: PresetUseMutationOptions<unknown, DeleteLabelGroupVariables>,
) => {
  const session = useWmgSession();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<unknown, HTTPError, DeleteLabelGroupVariables>({
    mutationFn: async (variables) => {
      return BFFClient.delete(`label-groups/${variables.id}`, {
        headers: getRequestHeaders(session.token),
      }).json();
    },

    onSuccess: async (data, variables, ctx) => {
      if (onSuccess) {
        onSuccess(data, variables, ctx);
      }

      /*
       todo: once API is updated, audit the cache invalidation logic
       maybe users and labels queries will need to be invalidated too
       if they have deleted group inside
       */
      queryClient.removeQueries({
        queryKey: LabelGroupQueryKeys.detail({ labelGroupId: variables.id }),
      });

      await queryClient.invalidateQueries({
        queryKey: LabelGroupQueryKeys.list({}),
      });
    },
    onError: (error) => {
      console.error('Label group delete failed:', error);
    },
    ...restOptions,
  });
};
